import { useExamEventDetails } from '@exam-center/exam-event-management';
import { FilterOperator, useSearchExamineeStatusesInExamEventQuery } from '@exam-center/graphql';
import { useParams, useNavigate } from '@exam-center/router';
import { Flex, Loading, useTheme } from '@shared/components';
import { useMediaQuery } from '@shared/hooks';

import { RegistrationExamineesDataGrid } from '../../components';
import { useProctoringRegistrationExamineeListSearchParams } from '../../hooks';

import { columns, mobileVariantColumns } from './columns';

export default function RegistrationExamineesList() {
  const navigate = useNavigate();
  const { examEventId, examCenterCode } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/registrations',
  );

  const { examEvent } = useExamEventDetails();

  const [{ page, pageSize, search, registrationStatus }, { changePageSize, changePageNumber }] =
    useProctoringRegistrationExamineeListSearchParams();

  const { data, loading } = useSearchExamineeStatusesInExamEventQuery({
    variables: {
      searchExamineeStatusesInExamEvent: {
        examEventId,
        searchExaminee: {
          filter: {
            searchText: {
              fields: ['fullName'],
              value: search,
            },
          },
        },
        filter: {
          conditions:
            registrationStatus.length > 0
              ? [
                  {
                    field: 'registrationStatus',
                    operator: FilterOperator.In,
                    value: registrationStatus,
                  },
                ]
              : undefined,
        },
        paging: {
          currentPage: page,
          pageSize,
        },
      },
    },
    pollInterval: 10000,
  });

  const theme = useTheme();
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('tablet'));

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(6),
      })}
    >
      {loading ? (
        <Loading />
      ) : (
        <RegistrationExamineesDataGrid
          columns={isTabletOrDesktop ? columns : mobileVariantColumns}
          onRowClick={(row) => {
            navigate(
              '/:examCenterCode/proctoring/exam-events/:examEventId/registrations/:examEventExamineeId',
              {
                params: {
                  examCenterCode: examCenterCode,
                  examEventId: examEventId,
                  examEventExamineeId: String(row.id),
                },
              },
            );
          }}
          slotProps={{
            footer: {
              total: data?.ecSearchExamineeStatusesInExamEvent.total,
              pagination: {
                page,
                count: data?.ecSearchExamineeStatusesInExamEvent.total,
                rowsPerPage: pageSize,
                onPageChange: (_, newPage) => {
                  changePageNumber(newPage);
                },
                onRowsPerPageChange: (event) => {
                  const newPageSize = parseInt(event.target.value, 10);
                  changePageSize(newPageSize);
                  changePageNumber(0);
                },
              },
            },
          }}
          rowCount={data?.ecSearchExamineeStatusesInExamEvent.total}
          rows={
            data?.ecSearchExamineeStatusesInExamEvent.examEventExaminees
              ? data.ecSearchExamineeStatusesInExamEvent.examEventExaminees.map(
                  (examEventExaminee) => {
                    return {
                      id: examEventExaminee.id,
                      examEventExamineeId: examEventExaminee.id,
                      seatNumber: examEventExaminee.seatNumber,
                      title: examEventExaminee.examinee?.title ?? '',
                      firstName: examEventExaminee.examinee?.firstName ?? '',
                      middleName: examEventExaminee.examinee?.middleName ?? '',
                      lastName: examEventExaminee.examinee?.lastName ?? '',
                      email: examEventExaminee.email,
                      examCourseCode: examEventExaminee.examCourse.code ?? '',
                      eKycStatus: examEventExaminee.examinee?.eKycStatus,
                      registrationStatus: examEventExaminee.registrationStatus,
                      registrationType: examEventExaminee.registrationType ?? '-',
                      examEventStartedAt: examEvent.examStartedAt,
                    };
                  },
                )
              : []
          }
        />
      )}
    </Flex>
  );
}
